import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AnalyticsParams, PeopleParams, PeopleSortBy } from 'modules/analytics';

export type DatePreset = 'today' | '7d' | '30d' | 'term';
export type AnalyticsTab = 'overview' | 'people' | 'tests' | 'integrity';

const DAY_MS = 86_400_000;
const PEOPLE_PER_PAGE = 25;

/** UTC start-of-day so query keys don't churn. `dateTo` is omitted — the backend defaults it to now. */
function presetToRange(preset: DatePreset): { dateFrom?: string } {
  if (preset === 'term') return { dateFrom: '1970-01-01T00:00:00.000Z' };
  const days = preset === 'today' ? 1 : preset === '7d' ? 7 : 30;
  const startOfToday = new Date();
  startOfToday.setUTCHours(0, 0, 0, 0);
  return { dateFrom: new Date(startOfToday.getTime() - (days - 1) * DAY_MS).toISOString() };
}

export interface AnalyticsFilters {
  tab: AnalyticsTab;
  preset: DatePreset;
  testId?: string;
  teamId?: string;
  page: number;
  sortBy: PeopleSortBy;
  sortDir: 'asc' | 'desc';
  q: string;
  params: AnalyticsParams;
  peopleParams: PeopleParams;
  setTab: (t: AnalyticsTab) => void;
  setPreset: (p: DatePreset) => void;
  setTestId: (id?: string) => void;
  setTeamId: (id?: string) => void;
  setPage: (p: number) => void;
  setSort: (by: PeopleSortBy, dir: 'asc' | 'desc') => void;
  setQ: (q: string) => void;
}

/** Persists analytics filters + active tab in the URL (deep-linkable, refresh-safe). */
export function useAnalyticsFilters(): AnalyticsFilters {
  const [sp, setSp] = useSearchParams();

  const tab = (sp.get('tab') as AnalyticsTab) || 'overview';
  const preset = (sp.get('preset') as DatePreset) || '30d';
  const testId = sp.get('testId') || undefined;
  const teamId = sp.get('teamId') || undefined;
  const page = Number(sp.get('page')) || 1;
  const sortBy = (sp.get('sortBy') as PeopleSortBy) || 'avgScore';
  const sortDir = (sp.get('sortDir') as 'asc' | 'desc') || 'desc';
  const q = sp.get('q') || '';

  const update = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value == null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  const dateRange = useMemo(() => presetToRange(preset), [preset]);
  const params = useMemo<AnalyticsParams>(() => ({ ...dateRange, testId, teamId }), [dateRange, testId, teamId]);
  const peopleParams = useMemo<PeopleParams>(
    () => ({ ...params, page, perPage: PEOPLE_PER_PAGE, sortBy, sortDir, q: q || undefined }),
    [params, page, sortBy, sortDir, q],
  );

  return {
    tab,
    preset,
    testId,
    teamId,
    page,
    sortBy,
    sortDir,
    q,
    params,
    peopleParams,
    setTab: (t) => update({ tab: t }),
    setPreset: (p) => update({ preset: p, page: undefined }),
    setTestId: (id) => update({ testId: id, page: undefined }),
    setTeamId: (id) => update({ teamId: id, page: undefined }),
    setPage: (p) => update({ page: p > 1 ? String(p) : undefined }),
    setSort: (by, dir) => update({ sortBy: by, sortDir: dir }),
    setQ: (value) => update({ q: value, page: undefined }),
  };
}
